import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { patientService } from '../services/patientService'
import { Patient } from '../types/patient'
import { formatPatientName } from '../utils/name'
import { formatDate } from '../utils/locale'
import './LinkedPatientRecords.css'

interface LinkedPatientRecordsProps {
  patient: Patient
  onUpdate?: () => void
}

export function LinkedPatientRecords({ patient, onUpdate }: LinkedPatientRecordsProps) {
  const queryClient = useQueryClient()
  const [expanded, setExpanded] = useState(true)
  const [unlinking, setUnlinking] = useState<string | null>(null)
  const [changingPrimary, setChangingPrimary] = useState(false)

  const isPrimary = patient.isPrimary ?? true
  const linkedRecords = patient.linkedRecords || []
  const primaryRecord = patient.primaryRecord

  const unlinkMutation = useMutation({
    mutationFn: async (linkedId: string) => {
      const primaryId = isPrimary ? patient.id : (patient.primaryPatientId || patient.id)
      await patientService.unlinkPatients(primaryId, linkedId)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patients', patient.id] })
      setUnlinking(null)
      onUpdate?.()
    },
    onError: (error) => {
      console.error('Error unlinking patients:', error)
      alert('Failed to unlink patients. Please try again.')
      setUnlinking(null)
    },
  })

  const setPrimaryMutation = useMutation({
    mutationFn: async (newPrimaryId: string) => {
      const currentPrimaryId = isPrimary ? patient.id : (patient.primaryPatientId || patient.id)
      await patientService.setPrimary(currentPrimaryId, newPrimaryId)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patients', patient.id] })
      setChangingPrimary(false)
      onUpdate?.()
    },
    onError: (error) => {
      console.error('Error changing primary:', error)
      alert('Failed to change primary designation. Please try again.')
      setChangingPrimary(false)
    },
  })

  const handleUnlink = async (linkedId: string) => {
    if (window.confirm('Are you sure you want to unlink these patient records?')) {
      setUnlinking(linkedId)
      unlinkMutation.mutate(linkedId)
    }
  }

  const handleSetPrimary = async (newPrimaryId: string) => {
    if (window.confirm('Are you sure you want to change the primary record designation?')) {
      setChangingPrimary(true)
      setPrimaryMutation.mutate(newPrimaryId)
    }
  }

  // If this is a secondary record, show primary record info
  if (!isPrimary && primaryRecord) {
    return (
      <div className="linked-records secondary">
        <div className="linked-records-header" onClick={() => setExpanded(!expanded)}>
          <h3>
            Linked to Primary Record
            <span className="expand-icon">{expanded ? '▼' : '▶'}</span>
          </h3>
        </div>
        {expanded && (
          <div className="linked-records-content">
            <div className="primary-record-info">
              <div className="record-badge primary">Primary</div>
              <div className="record-details">
                <h4>
                  <a href={`/patients/${primaryRecord.id}`} target="_blank" rel="noopener noreferrer">
                    {formatPatientName(primaryRecord)}
                  </a>
                </h4>
                <div className="record-meta">
                  <span>DOB: {formatDate(primaryRecord.dateOfBirth)}</span>
                  {primaryRecord.patientNumber && <span>Patient #: {primaryRecord.patientNumber}</span>}
                </div>
              </div>
              <div className="record-actions">
                <button
                  className="btn btn-primary"
                  onClick={() => handleSetPrimary(patient.id)}
                  disabled={changingPrimary}
                >
                  {changingPrimary ? 'Changing...' : 'Make This Primary'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    )
  }

  // If this is a primary record with linked records
  if (isPrimary && linkedRecords.length > 0) {
    return (
      <div className="linked-records primary">
        <div className="linked-records-header" onClick={() => setExpanded(!expanded)}>
          <h3>
            Linked Records ({linkedRecords.length})
            <span className="expand-icon">{expanded ? '▼' : '▶'}</span>
          </h3>
        </div>
        {expanded && (
          <div className="linked-records-content">
            <div className="current-record-info">
              <div className="record-badge primary">Primary</div>
              <div className="record-details">
                <h4>{formatPatientName(patient)}</h4>
                <div className="record-meta">
                  <span>DOB: {formatDate(patient.dateOfBirth)}</span>
                  {patient.patientNumber && <span>Patient #: {patient.patientNumber}</span>}
                </div>
              </div>
            </div>
            <div className="linked-records-list">
              <h4>Secondary Records</h4>
              {linkedRecords.map((linked) => {
                const isUnlinking = unlinking === linked.id
                return (
                  <div key={linked.id} className="linked-record-item">
                    <div className="record-badge secondary">Secondary</div>
                    <div className="record-details">
                      <h5>
                        <a href={`/patients/${linked.id}`} target="_blank" rel="noopener noreferrer">
                          {formatPatientName(linked)}
                        </a>
                      </h5>
                      <div className="record-meta">
                        <span>DOB: {formatDate(linked.dateOfBirth)}</span>
                        {linked.patientNumber && <span>Patient #: {linked.patientNumber}</span>}
                      </div>
                    </div>
                    <div className="record-actions">
                      <button
                        className="btn btn-primary"
                        onClick={() => handleSetPrimary(linked.id)}
                        disabled={changingPrimary}
                      >
                        {changingPrimary ? 'Changing...' : 'Make Primary'}
                      </button>
                      <button
                        className="btn btn-danger"
                        onClick={() => handleUnlink(linked.id)}
                        disabled={isUnlinking}
                      >
                        {isUnlinking ? 'Unlinking...' : 'Unlink'}
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )}
      </div>
    )
  }

  // No linked records
  return null
}
